/* istanbul ignore file */

import React, { useEffect, useState } from 'react';
import type { ConnectedProps } from 'react-redux';
import { batch, connect } from 'react-redux';
import { acceptsFunctionalCookies } from '@noths/polaris-client-gdpr-compliance';
import { parseLocaleSetCookieHeaders } from '@noths/polaris-client-localisation';
import type { Router } from '@noths/polaris-client-next-redux';
import * as recommendations from '@noths/polaris-client-recommendations';
import { Breakpoint, grid, maxWidthContainer } from '@noths/polaris-client-ribbons-base';
import { Media, NotificationInfo } from '@noths/polaris-client-ribbons-design-system';
import { visuallyHidden } from '@noths/polaris-client-styles';
import {
  actions as userConfigurationActions,
  getOrSetElevaateSessionId,
  getOrSetLoop54UserId,
  getOrSetRichRelevanceSessionId,
  getUserAttributes,
} from '@noths/polaris-client-user-configuration';
import { addRUMTiming, generateRandomString } from '@noths/polaris-client-utils';
import { StatusCode } from '@noths/polaris-dev-ts-types';
import type { GetServerSideProps } from 'next';
import getConfig from 'next/config';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { parse } from 'url';

import { BreadcrumbContainer } from 'src/components/molecules/Breadcrumb';
import { ListingsApiErrorGuardContainer } from 'src/components/molecules/ListingsApiErrorGuard';
import { FilterContainer } from 'src/components/organisms/Filter';
import { FilterUid } from 'src/components/organisms/Filter/constants/uids';
import { QuickFiltersContainer } from 'src/components/organisms/Filter/containers/QuickFiltersContainer';
import { SlimFilterMenuContainer } from 'src/components/organisms/FilterMenu';
import { HeaderCopyBlockContainer } from 'src/components/organisms/HeaderCopyBlock';
import { ProductPagesContainer } from 'src/components/organisms/ProductPages';
import { ERROR_CATEGORY_NOT_FOUND } from 'src/constants/errors';
import { Placement } from 'src/constants/recommendations';
import { useFilterNavType } from 'src/hooks/useFilterNavType';
import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { actions as navigationActions } from 'src/redux/navigation';
import { selectCurrentPage } from 'src/redux/products/selectors';
import { selectPagePropsForClientHydration, selectPaginationTagValues } from 'src/redux/selectors';
import { getStore, useStore } from 'src/redux/store';
import { fetchGlobalContent } from 'src/services/contentstack';
import * as redirection from 'src/services/redirection';
import * as styles from 'src/styles/pages.styles';
import type { NextConfig, PageProps } from 'src/types';
import { PageType } from 'src/types';
import * as elementIds from '../constants/elementIds';
import type { ProductsState } from '../redux/products';
import { actions as productActions } from '../redux/products';
import { requestProducts } from '../redux/products/thunks/requestProducts';
const { publicRuntimeConfig, serverRuntimeConfig } = getConfig() as NextConfig;
import type { InternalLinkGroups } from '@noths/polaris-internal-links';

import { recommendedProductsRequestComplete } from 'src/redux/richRelevance/actions';
import { requestRecommendedProducts } from 'src/redux/richRelevance/thunks';
import { getInternalLinksForCLP } from 'src/services/internalLinks/getInternalLinksForCLP';
import { BackToTopButton } from '../components/molecules/BackToTopButton';
import {
  LinkCarousel,
  LinkCarouselTrackingCategory,
  LinkCarouselTrackingLinkSource,
} from '../components/organisms/LinkCarousel';
import { RELATED_CATEGORIES_TITLE } from '../components/organisms/ProductPages/constants/copy';
import { RecommendedProductsShelfContainer } from '../components/organisms/RecommendedProductsShelf';
import { getFeatureFlagsWithMatchers } from '../utils/getFeatureFlagsWithMatchers';
import { getProductCodesFromPlacementItem } from '../utils/recommendations';

const mapStateToProps = (state: ReduxApplicationState) => ({
  products: state.products,
  currentPage: selectCurrentPage(state),
  paginationTagValues: selectPaginationTagValues(state),
  pageLiveRegionText: state.accessibility.statusMessage,
});

const connector = connect(mapStateToProps);

type CategoryPageProps = ProductsState &
  PageProps &
  ConnectedProps<typeof connector> & {
    hideRecentlyViewedPlacement: boolean;
    topInternalLinks: { text: string; href: string }[];
    bottomInternalLinks: { text: string; href: string }[];
  };
const CategoryPage = ({
  products,
  currentPage,
  paginationTagValues,
  updatedReduxState,
  functionalCookiesConsented,
  pageLiveRegionText,
  topInternalLinks = [],
  bottomInternalLinks = [],
}: CategoryPageProps) => {
  const { query } = useRouter();
  const { dispatch } = useStore();
  const [redirectNotification, setRedirectNotification] = useState(false);

  const category = products.category!;
  const { id, metaTagDescription, pageTitle, name: categoryName, seoCategories } = category;
  const isDontGiftGuideCategory = category.canonicalTag.includes('/christmas/dont-gift-guide');
  const canonicalHref = isDontGiftGuideCategory
    ? 'https://www.notonthehighstreet.com/dontgiftguide'
    : category.canonicalTag;
  const indexable = isDontGiftGuideCategory ? false : category.indexable;

  useEffect(() => {
    getUserAttributes()
      .then((attributes) =>
        recommendations.initRecommendations({
          apiKey: publicRuntimeConfig.RICH_RELEVANCE_API_KEY,
          env: process.env.NODE_ENV,
          hasDisabledCookies: !functionalCookiesConsented,
          sessionId: updatedReduxState?.userConfiguration?.richRelevanceSessionId,
          userId: attributes?.sub,
        }),
      )
      .then(async () => {
        recommendations.setCategoryPage(id.toString(), categoryName);

        if (products.items[0]) {
          const productCodes = getProductCodesFromPlacementItem(products.items[0]);

          recommendations.setCategoryProductCodes(productCodes);
        }

        const placementData = await recommendations.getRecommendations([
          Placement.CategoryListingsRecentlyViewed,
        ]);

        const recommendedProductsRequests = placementData.map((placement) =>
          dispatch(requestRecommendedProducts(placement)),
        );

        await Promise.allSettled(recommendedProductsRequests);
        addRUMTiming('recommendationsLoaded');
      })
      .catch((e) => {
        console.warn(`Could not load recommendations on category listings page: ${e.message}`);
      })
      .finally(() => {
        dispatch(recommendedProductsRequestComplete());
      });
  });

  const [prevMetaTag, nextMetaTag] = paginationTagValues;
  const hasBottomInternalLinks = bottomInternalLinks.length > 0;

  const bottomLinks = hasBottomInternalLinks
    ? bottomInternalLinks
    : seoCategories.map((seoCategory) => ({
        href: seoCategory.rel!,
        text: seoCategory.title!,
      }));

  const trackingLinkDataSource = hasBottomInternalLinks
    ? LinkCarouselTrackingLinkSource.SimilarAI
    : LinkCarouselTrackingLinkSource.Browse;

  useFilterNavType((filterNavType) =>
    dispatch(navigationActions.setInitialPageLoad(filterNavType)),
  );

  useEffect(() => {
    if (query.redirectedFrom === 'pdp') {
      setRedirectNotification(true);
    }
  }, [query.redirectedFrom]);

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
        <meta name="description" content={metaTagDescription} />
        <link rel="canonical" href={canonicalHref} />
        {!indexable ? (
          <meta name="robots" content="noindex" />
        ) : (
          currentPage > 1 && <meta name="robots" content="noindex,follow" />
        )}
        {prevMetaTag && <link rel="prev" href={prevMetaTag} />}
        {nextMetaTag && <link rel="next" href={nextMetaTag} />}
        <link rel="preconnect" href={`${publicRuntimeConfig.hosts.elevaate}`} />
      </Head>
      <ListingsApiErrorGuardContainer>
        <div css={visuallyHidden} role="status">
          {pageLiveRegionText}
        </div>
        <div css={styles.wrapper}>
          <div css={[grid, maxWidthContainer]}>
            <div css={styles.breadcrumbContainer}>
              <BreadcrumbContainer />
              {redirectNotification && (
                <NotificationInfo>
                  We don&apos;t seem to have that product anymore, but here are some similar items
                  below.
                </NotificationInfo>
              )}
              <HeaderCopyBlockContainer internalLinks={topInternalLinks} />
            </div>
          </div>
          <div id={elementIds.PRODUCT_CONTAINER_WRAPPER}>
            <SlimFilterMenuContainer />
            <Media lessThan={Breakpoint.L}>
              <div css={styles.quickFiltersWrapper}>
                <QuickFiltersContainer filterUid={FilterUid.Delivery} />
              </div>
            </Media>
            <div css={[grid, maxWidthContainer, styles.productListWrapper]}>
              <div css={styles.productListSidebar}>
                <FilterContainer />
              </div>
              <div css={styles.productPagesWrapper}>
                <ProductPagesContainer />
                {bottomLinks.length && (
                  <div css={styles.relatedCategories}>
                    <LinkCarousel
                      title={RELATED_CATEGORIES_TITLE}
                      trackingCategory={LinkCarouselTrackingCategory.RelatedCategory}
                      links={bottomLinks}
                      trackingLinkDataSource={trackingLinkDataSource}
                    />
                  </div>
                )}
                <RecommendedProductsShelfContainer
                  placementType={Placement.CategoryListingsRecentlyViewed}
                />
                <BackToTopButton />
              </div>
            </div>
          </div>
        </div>
      </ListingsApiErrorGuardContainer>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async ({ query, res, req }) => {
  const { category, ...queryParams } = query;

  const pathname = parse(req.url!).pathname!;

  if (Array.isArray(category)) {
    const routerState: Router = { pathname, query: queryParams };
    const {
      currency,
      delivery_zone,
      _elevaate_session_id,
      cognitoId,
      Loop54User,
      rr_session,
      _ga: googleAnalyticsVisitorId,
    } = req.cookies;
    const gaVisitorId = googleAnalyticsVisitorId ?? generateRandomString(10);
    const functionalCookiesConsented = acceptsFunctionalCookies(req.cookies);
    const { dispatch, getState } = getStore(
      {
        router: routerState,
      },
      {
        userConfiguration: {
          gaVisitorId,
        },
        navigation: {
          pageType: PageType.Category,
        },
      },
    );
    const richRelevanceSessionId = getOrSetRichRelevanceSessionId(res, rr_session);
    const featureFlags = getFeatureFlagsWithMatchers(req);
    const { currency: initialCurrency, deliveryZone: initialDeliveryZone } =
      parseLocaleSetCookieHeaders(res.getHeaders());

    batch(() => {
      dispatch(productActions.setInitialProductPage(Number(queryParams.page ?? 1)));
      dispatch(navigationActions.setPathname({ url: req.url ?? '', host: req.headers.host ?? '' }));
      dispatch(userConfigurationActions.setCurrencyCode(initialCurrency ?? currency));
      dispatch(userConfigurationActions.setDeliveryZone(initialDeliveryZone ?? delivery_zone));
      dispatch(userConfigurationActions.setCognitoId(cognitoId));
      dispatch(userConfigurationActions.setFeatureFlags(featureFlags));
      dispatch(userConfigurationActions.setRichRelevanceSessionId(richRelevanceSessionId));

      if (functionalCookiesConsented) {
        const elevaateSessionId = getOrSetElevaateSessionId(res, _elevaate_session_id);
        const loop54UserId = getOrSetLoop54UserId(res, Loop54User);

        dispatch(userConfigurationActions.setElevaateSessionId(elevaateSessionId));
        dispatch(userConfigurationActions.setLoop54UserId(loop54UserId));
      }
    });

    const productsQuery = {
      ...queryParams,
      path: pathname,
    };

    const urlPath = req.url;

    const [[navData, topBannerData], internalLinkGroups] = await Promise.all([
      fetchGlobalContent({ featureFlags, urlPath }),
      getInternalLinksForCLP(pathname),
      dispatch(
        requestProducts({
          cookie: req.headers.cookie,
          query: productsQuery,
          isHealthcheckRequest: req.headers['health-check'] === 'true',
        }),
      ),
    ]);

    const state = getState();
    const { products } = state;

    const redirect = redirection.productGuard(products, query, pathname);

    if (redirect) {
      return redirect;
    }

    if (products.error) {
      res.statusCode =
        products.error.name === ERROR_CATEGORY_NOT_FOUND
          ? StatusCode.NotFound
          : StatusCode.InternalServerError;

      if (res.statusCode === StatusCode.InternalServerError) {
        res.setHeader('Retry-After', serverRuntimeConfig.retryAfterDuration);
      }

      const props: PageProps = {
        featureFlags,
        navData,
        functionalCookiesConsented,
        updatedReduxState: {
          // Cherry pick the state to be updated on the client
          products,
          navigation: { pageType: PageType.Category },
        },
      };

      return {
        props,
      };
    }

    const props = {
      featureFlags,
      navData,
      topBannerData,
      functionalCookiesConsented,
      ...selectPagePropsForClientHydration(state),
      ...((internalLinkGroups as InternalLinkGroups)?.top
        ? { topInternalLinks: (internalLinkGroups as InternalLinkGroups)?.top }
        : {}),
      ...((internalLinkGroups as InternalLinkGroups)?.bottom
        ? { bottomInternalLinks: (internalLinkGroups as InternalLinkGroups)?.bottom }
        : {}),
    };

    return {
      props,
    };
  } else {
    const props: PageProps = {
      updatedReduxState: {
        products: {
          error: {
            message: 'An unknown error occurred',
          },
        },
      },
    };

    return {
      props,
    };
  }
};

export default connector(CategoryPage);
